// src/features/settings/components/SettingsPage.jsx
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

import AppDialog from '../../../shared/components/AppDialog';
import DataState from '../../../shared/components/DataState';
import Icon from '../../../shared/components/Icon';
import AppToast from '../../../shared/components/AppToast';
import Dropdown from '../../../shared/components/Dropdown';
import LabeledField from '../../../shared/components/LabeledField';
import useEscapeKeyClose from '../../../shared/hooks/useEscapeKeyClose';
import { CategoryIcon } from '../../../shared/components/Badges';
import { applyApiFieldErrors } from '../../../shared/api';
import { resolveUserFacingError } from '../../../shared/utils/actionFeedback';
import { usePagePresentation } from '../../../context/PagePresentationContext';
import {
  fetchUsers, createUser, updateUser, resetUserPassword,
  fetchRoles,
  adminFetchCategories, adminCreateCategory, adminUpdateCategory, adminDeleteCategory,
} from '../../../api/carbonApi';
import '../../../styles/common.css';
import '../../../styles/modal.css';
import '../../../styles/forms.css';
import '../../../styles/table.css';
import '../styles/settings.css';

// â”€â”€ Mini design system â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function colorFromString(str) {
  const palette = ['#4f46e5', '#0284c7', '#0d9488', '#16a34a', '#ca8a04', '#dc2626', '#9333ea', '#0891b2', '#7c3aed', '#e11d48'];
  let h = 0;
  for (let i = 0; i < (str || '').length; i++) h = (h * 31 + str.charCodeAt(i)) % palette.length;
  return palette[Math.abs(h) % palette.length];
}

function initials(name) {
  return (name || '?').split(' ').filter(Boolean).map(s => s[0]).slice(0, 2).join('').toUpperCase();
}

function Avatar({ name, email, size = 32 }) {
  const label = name || email || '?';
  const bg = colorFromString(label);
  const fontSize = size * 0.36;
  return (
    <div
      className="settings-avatar"
      style={{ width: size, height: size, fontSize, '--avatar-color': bg }}
    >
      {initials(label)}
    </div>
  );
}

function UserCell({ name, email }) {
  const displayName = name || (email ? email.split('@')[0] : '—');
  return (
    <div className="settings-user-cell">
      <Avatar name={displayName} email={email} />
      <div>
        <div className="settings-user-cell__name">{displayName}</div>
        {email && <div className="settings-user-cell__email">{email}</div>}
      </div>
    </div>
  );
}

function StatusBadge({ active }) {
  const { t } = useTranslation();
  return active
    ? <span className="settings-status settings-status--active"><span className="settings-status__dot settings-status__dot--active" />{t('common.active')}</span>
    : <span className="settings-status settings-status--inactive"><span className="settings-status__dot settings-status__dot--inactive" />{t('common.inactive')}</span>;
}

function Pill({ text, color }) {
  const c = color || '#4f46e5';
  return (
    <span
      className="settings-pill"
      style={{ background: c + '15', color: c, borderColor: c + '25' }}
    >{text}</span>
  );
}

function userTypeLabel(userType) {
  return userType === 'NON_FJP' ? 'NON FJP' : (userType || 'FJP');
}

// â”€â”€ Shared UI helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function Modal({ title, subtitle, icon, onClose, children, width = 500 }) {
  const { t } = useTranslation();
  useEscapeKeyClose({ onClose });
  return createPortal(
    <div className="modal-backdrop">
      <div className="modal-absolute__panel" style={{ width }} onClick={e => e.stopPropagation()}>
        <div className="modal-absolute__header">
          <div className="modal-absolute__header-left">
            {icon && (
              <div className="modal-icon">
                {icon}
              </div>
            )}
            <div>
              <h3 className="modal-absolute__title">{title}</h3>
              {subtitle && <p className="modal-absolute__subtitle">{subtitle}</p>}
            </div>
          </div>
          <button className="modal-absolute__close" onClick={onClose} aria-label={t('common.close')}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}

function IconField({ icon, color, align = 'center', children }) {
  return (
    <div className="icon-field">
      <span className={`icon-field__icon${align === 'top' ? ' icon-field__icon--top' : ''}`} style={{ color: color || 'var(--muted)' }}>
        <Icon name={icon} size={15} />
      </span>
      {children}
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div className="field-group">
      <label className={`field-group__label${required ? ' field-group__label--required' : ''}`}>
        {label}
      </label>
      {children}
      {error && (
        <Typography
          component={'p'}
          variant={'caption'}
          color={'error'}
          role={'alert'}
          sx={{ mt: 0.5 }}
        >
          {error}
        </Typography>
      )}
    </div>
  );
}


const USER_FORM_FIELDS = Object.freeze({
  full_name: 'full_name',
  email: 'email',
  password: 'password',
  new_password: 'password',
  department: 'department',
  job_title: 'job_title',
  role_ref_id: 'role_ref_id',
  user_type: 'user_type',
  is_active: 'is_active',
});

const CATEGORY_FORM_FIELDS = Object.freeze({
  category_name: 'category_name',
  description: 'description',
  sample: 'sample',
  active: 'active',
  icon: 'icon',
  color: 'color',
});

function collectApiFormErrors(error, fieldAliases, t) {
  const fields = {};
  const rootMessages = [];
  const hasFieldErrors = applyApiFieldErrors(error, (field, detail) => {
    const formField = fieldAliases[field];
    if (formField) {
      if (fields[formField] === undefined || fields[formField] === null) {
        fields[formField] = detail.message;
      }
      return;
    }
    rootMessages.push(detail.message);
  }, t);

  return {
    fields,
    hasFieldErrors,
    root: rootMessages[0] || '',
  };
}

function closeActionModal(setModal, setToast) {
  setModal(null);
  setToast(null);
}

function ErrorBanner({ msg }) {
  if (!msg) return null;
  return (
    <div className="error-banner">
      <WarningAmberRoundedIcon className="error-banner__icon" fontSize="small" />{msg}
    </div>
  );
}

function ConfirmDialog({ message, onConfirm, onCancel }) {
  const { t } = useTranslation();
  return (
    <Modal title={t('settings.confirmDeleteTitle')} onClose={onCancel}>
      <p className="confirm-dialog__message">{message}</p>
      <div className="flex-gap-8 flex-center confirm-dialog__footer">
        <button className="btn-ghost" onClick={onCancel}>{t('common.cancel')}</button>
        <button className="confirm-dialog__delete-btn" onClick={onConfirm}>{t('settings.delete')}</button>
      </div>
    </Modal>
  );
}

function EmptyState({ message }) {
  return (
    <tr>
      <td colSpan={99} className="table-empty">
        <div className="table-empty__icon">ðŸ“­</div>
        <div className="table-empty__text">{message}</div>
      </td>
    </tr>
  );
}

function LoadingState() {
  const { t } = useTranslation();
  return (
    <tr>
      <td aria-hidden="true" colSpan={99} className="table-loading" style={{ display: 'none' }}>
        {t('common.loading')}
      </td>
    </tr>
  );
}

// â”€â”€ Users Tab â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const EMPTY_USER = {
  full_name: '',
  email: '',
  password: '',
  department: '',
  job_title: '',
  role_ref_id: '',
  user_type: 'FJP',
  is_active: true
};

function UsersTab() {
  const { t } = useTranslation();
  const isMountedRef = useRef(true);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_USER);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [toast, setToast] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  usePagePresentation({ loading });

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (!isMountedRef.current) {
      return;
    }

    setLoading(true);
    setLoadError(null);
    setUsers([]);
    setRoles([]);
    try {
      const [uRes, rRes] = await Promise.all([fetchUsers(), fetchRoles()]);
      if (!isMountedRef.current) {
        return;
      }

      setUsers(uRes.items || []);
      setRoles(rRes.items || []);
    } catch (requestError) {
      if (isMountedRef.current) {
        setLoadError(requestError);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const openAdd = () => {
    setForm(EMPTY_USER);
    setFormError('');
    setFieldErrors({});
    setToast(null);
    setModal({ mode: 'add' });
  };
  const openEdit = item => {
    setForm({
      full_name: item.full_name || '',
      email: item.email || '',
      department: item.department || '',
      job_title: item.job_title || '',
      role_ref_id: item.role_ref_id || '',
      user_type: item.user_type || 'FJP',
      password: '',
      is_active: item.is_active !== false,
    });
    setFormError('');
    setFieldErrors({});
    setToast(null);
    setModal({ mode: 'edit', item });
  };

  const closeUserModal = () => {
    if (saving) return;
    closeActionModal(setModal, setToast);
  };

  const setUserFormField = (field, value) => {
    setForm(current => ({ ...current, [field]: value }));
    setFieldErrors(current => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
    setFormError('');
  };

  const save = async () => {
    const clientErrors = {};
    if (!form.email) clientErrors.email = t('settings.validation.emailRequired');
    else if (!form.email.toLowerCase().endsWith('@fpt.com')) {
      clientErrors.email = t('settings.validation.emailDomain');
    }
    if (!form.role_ref_id) clientErrors.role_ref_id = t('settings.validation.roleRequired');
    if (form.password && form.password.length < 8) {
      clientErrors.password = t('settings.validation.passwordTooShort');
    }
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      setFormError('');
      setToast(null);
      return;
    }

    const action = modal.mode === 'add' ? 'create' : 'update';
    setSaving(true);
    setFormError('');
    setFieldErrors({});
    setToast(null);
    try {
      const payload = {
        full_name: form.full_name || undefined,
        email: form.email,
        department: form.department || undefined,
        job_title: form.job_title || undefined,
        role_ref_id: form.role_ref_id,
        user_type: form.user_type,
        is_active: form.is_active,
      };
      if (modal.mode === 'add') {
        if (form.password) payload.password = form.password;
        await createUser(payload);
      }
      else {
        await updateUser(modal.item.id, payload);
        if (form.password) await resetUserPassword(modal.item.id, form.password);
      }
      if (!isMountedRef.current) {
        return;
      }

      setToast({
        severity: 'success',
        message: t(`common.feedback.${action}.success`),
      });
      setModal(null);
      void load();
    } catch (actionError) {
      if (!isMountedRef.current) {
        return;
      }

      const mappedErrors = collectApiFormErrors(actionError, USER_FORM_FIELDS, t);
      if (mappedErrors.hasFieldErrors) {
        setFieldErrors(mappedErrors.fields);
        setFormError(mappedErrors.root);
        return;
      }

      setFormError(resolveUserFacingError(
        actionError,
        t(`common.feedback.${action}.failure`),
      ));
    } finally {
      if (isMountedRef.current) {
        setSaving(false);
      }
    }
  };

  const roleName = id => roles.find(r => r.id === id)?.role_name || '—';

  function handleRoleChange(nextRoleId) {
    setUserFormField('role_ref_id', nextRoleId);
  }

  function handleUserTypeChange(nextUserType) {
    setUserFormField('user_type', nextUserType);
  }

  const availableRoles = roles;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedUsers = users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Paper
      component="section"
      variant="outlined"
      aria-labelledby="settings-users-title"
      className="settings-users-card"
      sx={{ minWidth: 0, overflow: 'hidden' }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ px: { xs: 2, md: 2.5 }, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Box>
          <Typography id="settings-users-title" component="h2" variant="subtitle1" sx={{ fontWeight: 750 }}>
            {t('settings.usersTitle')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('settings.userCount', { count: users.length })}
          </Typography>
        </Box>
        <Button
          type="button"
          variant="contained"
          startIcon={<Icon name="plus" size={15} />}
          onClick={openAdd}
          disabled={loading || Boolean(loadError)}
          sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
        >
          {t('settings.addUser')}
        </Button>
      </Stack>
      {(loading || loadError) && (
        <Box sx={{ px: { xs: 2, md: 2.5 }, pt: 2 }}>
          <DataState
            loading={loading}
            error={loadError}
            onRetry={load}
            errorMessage={t('settings.error.loadUsers')}
          />
        </Box>
      )}

      {!loadError && (
        <TableContainer
          sx={{ overflowX: 'auto' }}
        >
          <Table className="data-table" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell className="settings-table__th-index">#</TableCell>
                <TableCell>{t('settings.columns.user')}</TableCell>
                <TableCell>{t('settings.columns.userType')}</TableCell>
                <TableCell>{t('settings.columns.department')}</TableCell>
                <TableCell>{t('settings.columns.jobTitle')}</TableCell>
                <TableCell>{t('settings.columns.role')}</TableCell>
                <TableCell className="settings-table__th-status">{t('common.status')}</TableCell>
                <TableCell className="settings-table__th-actions" align="center">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && users.length === 0 && <LoadingState />}
              {paginatedUsers.map((item, idx) => (
                <TableRow key={item.id}>
                  <TableCell className="table-cell--index">
                    {page * rowsPerPage + idx + 1}
                  </TableCell>
                  <TableCell>
                    <UserCell name={item.full_name} email={item.email} />
                  </TableCell>
                  <TableCell><Pill text={userTypeLabel(item.user_type)} color="#0d9488" /></TableCell>
                  <TableCell>{item.department ? <Pill text={item.department} color="#0284c7" /> : <span className="text-muted">—</span>}</TableCell>
                  <TableCell>{item.job_title || '—'}</TableCell>
                  <TableCell>{item.role_ref_id ? <Pill text={roleName(item.role_ref_id)} color="#7c3aed" /> : <span className="text-muted">—</span>}</TableCell>
                  <TableCell><StatusBadge active={item.is_active !== false} /></TableCell>
                  <TableCell align="center">
                    <div className="settings-actions">
                      <button className="btn-ghost-sm" onClick={() => openEdit(item)} title={t('common.edit')}><Icon name="edit" size={13} /></button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && users.length === 0 && <EmptyState message={t('settings.emptyUsers')} />}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            count={users.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t('common.rowsPerPage')}
            sx={{
              borderTop: '1px solid var(--border)',
              '& .MuiTablePagination-toolbar': {
                minHeight: '52px'
              }
            }}
          />
        </TableContainer>
      )}

      {modal && (
        <AppDialog
          open
          onClose={saving ? undefined : closeUserModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ pr: 6 }}>
            {t(modal.mode === 'add' ? 'settings.addUser' : 'settings.editUser')}
            <IconButton
              onClick={closeUserModal}
              disabled={saving}
              aria-label={t('common.close')}
              sx={{ position: 'absolute', right: 12, top: 12 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            {formError && <Alert severity="error" sx={{ mb: 2.5 }}>{formError}</Alert>}
            <Grid
              container
              spacing={2.5}
              component="form"
              id="user-form"
              onSubmit={(event) => { event.preventDefault(); save(); }}
            >
              <Grid item xs={12} md={6}>
                <LabeledField label={t('settings.fields.fullName')} htmlFor="full_name" required error={fieldErrors.full_name}>
                  <OutlinedInput
                    id="full_name"
                    size="small"
                    value={form.full_name}
                    onChange={event => setUserFormField('full_name', event.target.value)}
                    placeholder={t('settings.placeholders.fullName')}
                  />
                </LabeledField>
              </Grid>

              <Grid item xs={12} md={6}>
                <LabeledField label={t('settings.fields.email')} htmlFor="email" required error={fieldErrors.email}>
                  <OutlinedInput
                    id="email"
                    size="small"
                    type="email"
                    value={form.email}
                    onChange={event => setUserFormField('email', event.target.value)}
                    placeholder="account@fpt.com"
                  />
                </LabeledField>
              </Grid>

              <Grid item xs={12} md={6}>
                <LabeledField label={t('settings.fields.password')} htmlFor="password" error={fieldErrors.password}>
                  <OutlinedInput
                    id="password"
                    size="small"
                    type="password"
                    autoComplete="new-password"
                    value={form.password}
                    onChange={event => setUserFormField('password', event.target.value)}
                    placeholder={t('settings.placeholders.optionalPassword')}
                  />
                </LabeledField>
              </Grid>

              <Grid item xs={12} md={6}>
                <LabeledField label={t('settings.fields.jobTitle')} htmlFor="job_title" error={fieldErrors.job_title}>
                  <OutlinedInput
                    id="job_title"
                    size="small"
                    value={form.job_title}
                    onChange={event => setUserFormField('job_title', event.target.value)}
                    placeholder={t('settings.placeholders.jobTitle')}
                  />
                </LabeledField>
              </Grid>

              <Grid item xs={12} md={6}>
                <LabeledField label={t('settings.fields.department')} htmlFor="department" error={fieldErrors.department}>
                  <OutlinedInput
                    id="department"
                    size="small"
                    value={form.department}
                    onChange={event => setUserFormField('department', event.target.value)}
                    placeholder={t('settings.placeholders.department')}
                  />
                </LabeledField>
              </Grid>

              <Grid item xs={12} md={6}>
                <LabeledField label={t('settings.fields.userType')} htmlFor="user_type" required error={fieldErrors.user_type}>
                  <Select
                    id="user_type"
                    size="small"
                    value={form.user_type}
                    onChange={event => handleUserTypeChange(event.target.value)}
                  >
                    <MenuItem value="FJP">FJP</MenuItem>
                    <MenuItem value="NON_FJP">NON FJP</MenuItem>
                  </Select>
                </LabeledField>
              </Grid>

              <Grid item xs={12} md={6}>
                <LabeledField label={t('settings.fields.role')} htmlFor="role_ref_id" required error={fieldErrors.role_ref_id}>
                  <Select
                    id="role_ref_id"
                    size="small"
                    value={form.role_ref_id}
                    onChange={event => handleRoleChange(event.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>{t('settings.selectRole')}</MenuItem>
                    {availableRoles.map(role => (
                      <MenuItem key={role.id} value={role.id}>{role.role_name}</MenuItem>
                    ))}
                  </Select>
                </LabeledField>
              </Grid>

              <Grid item xs={12} md={6}>
                <LabeledField label={t('settings.fields.status')} htmlFor="is_active" error={fieldErrors.is_active}>
                  <Select
                    id="is_active"
                    size="small"
                    value={form.is_active ? 'active' : 'inactive'}
                    onChange={event => setUserFormField('is_active', event.target.value === 'active')}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </LabeledField>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={closeUserModal} disabled={saving}>{t('common.cancel')}</Button>
            <Button type="submit" form="user-form" variant="contained" disabled={saving}>
              {t(modal.mode === 'add'
                ? (saving ? 'common.creating' : 'common.create')
                : (saving ? 'common.updating' : 'common.update'))}
            </Button>
          </DialogActions>
        </AppDialog>
      )}

      <AppToast toast={toast} onClose={() => setToast(null)} />
    </Paper>
  );
}

// â”€â”€ Category icon/color pickers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const CATEGORY_ICON_OPTIONS = [
  'tag', 'key', 'lock', 'globe', 'monitor', 'alert', 'package', 'mail', 'briefcase', 'building',
  'map', 'users', 'user', 'send', 'sparkle', 'list', 'paperclip', 'more', 'flame', 'inbox',
  'message', 'refresh', 'check-circle', 'x-circle', 'pause-circle', 'plus-circle', 'clock',
  'search', 'bell', 'chart', 'settings',
];

function IconPicker({ value, onChange, color }) {
  return (
    <div className="icon-picker">
      {CATEGORY_ICON_OPTIONS.map(name => {
        const selected = value === name;
        return (
          <button
            key={name}
            type="button"
            className={`icon-picker__btn${selected ? ' icon-picker__btn--selected' : ''}`}
            style={selected ? { '--picker-color': color } : {}}
            onClick={() => onChange(name)}
            title={name}
          >
            <Icon name={name} size={16} />
          </button>
        );
      })}
    </div>
  );
}

const CATEGORY_COLOR_OPTIONS = [
  '#e11d48', '#dc2626', '#ea580c', '#d97706', '#ca8a04', '#65a30d', '#16a34a', '#059669',
  '#0d9488', '#0891b2', '#0284c7', '#4f46e5', '#7c3aed', '#9333ea', '#c026d3', '#db2777',
  '#525252', '#6b7280',
];

function ColorPicker({ value, onChange }) {
  const { t } = useTranslation();
  return (
    <div className="color-picker">
      <div className="color-picker__swatches">
        {CATEGORY_COLOR_OPTIONS.map(hex => (
          <button
            key={hex}
            type="button"
            className={`color-picker__swatch${value === hex ? ' color-picker__swatch--selected' : ''}`}
            style={value === hex ? { '--swatch-color': hex } : { background: hex }}
            onClick={() => onChange(hex)}
            title={hex}
          />
        ))}
      </div>
      <label className="color-picker__custom">
        {t('settings.categories.customColor')}
        <input
          type="color"
          className="color-picker__custom-input"
          value={value}
          onChange={e => onChange(e.target.value)}
        />
      </label>
    </div>
  );
}

// â”€â”€ Category Tab â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const EMPTY_CAT = { category_name: '', description: '', sample: '', active: true, icon: 'tag', color: '#6b7280' };

function CategoryTab({ onCategoriesChanged }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_CAT);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [toast, setToast] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setItems([]);
    try {
      const res = await adminFetchCategories();
      const sorted = [...(res.items || [])].sort((a, b) => (a.label || '').localeCompare(b.label || ''));
      setItems(sorted);
    }
    catch (requestError) { setLoadError(requestError); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const openAdd = () => {
    setForm(EMPTY_CAT);
    setFormError('');
    setFieldErrors({});
    setToast(null);
    setModal({ mode: 'add' });
  };
  const openEdit = item => {
    setForm({
      id: item.id,
      category_name: item.category_name,
      description: item.description || '',
      sample: item.sample || '',
      active: item.active,
      icon: item.icon || 'tag',
      color: item.color || '#6b7280',
    });
    setFormError('');
    setFieldErrors({});
    setToast(null);
    setModal({ mode: 'edit', item });
  };

  const setCategoryFormField = (field, value) => {
    setForm(current => ({ ...current, [field]: value }));
    setFieldErrors(current => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
    setFormError('');
  };

  const save = async () => {
    if (!form.category_name) {
      setFieldErrors({
        category_name: t('settings.validation.categoryNameRequired'),
      });
      setFormError('');
      setToast(null);
      return;
    }
    const action = modal.mode === 'add' ? 'create' : 'update';
    setSaving(true);
    setFormError('');
    setFieldErrors({});
    setToast(null);
    try {
      const payload = { category_name: form.category_name, description: form.description, sample: form.sample, icon: form.icon, color: form.color };
      if (modal.mode === 'add') await adminCreateCategory(payload);
      else await adminUpdateCategory(modal.item.id, { ...payload, active: form.active });
      setToast({
        severity: 'success',
        message: t(`common.feedback.${action}.success`),
      });
      setModal(null); void load(); onCategoriesChanged?.();
    } catch (actionError) {
      const mappedErrors = collectApiFormErrors(actionError, CATEGORY_FORM_FIELDS, t);
      if (mappedErrors.hasFieldErrors) {
        setFieldErrors(mappedErrors.fields);
        setFormError(mappedErrors.root);
        return;
      }

      setToast({
        severity: 'error',
        message: resolveUserFacingError(
          actionError,
          t(`common.feedback.${action}.failure`),
        ),
      });
    }
    finally { setSaving(false); }
  };

  const doDelete = async (id) => {
    try {
      await adminDeleteCategory(id);
      setToast({ severity: 'success', message: t('common.feedback.delete.success') });
      void load();
      onCategoriesChanged?.();
    } catch (actionError) {
      setToast({
        severity: 'error',
        message: resolveUserFacingError(
          actionError,
          t('common.feedback.delete.failure'),
        ),
      });
    } finally {
      setConfirm(null);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedItems = items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <div>
      <div className="settings-tab-header">
        <p className="settings-tab-header__count">{t('settings.categories.count', { count: items.length })}</p>
        <button
          className="btn-primary"
          onClick={openAdd}
          disabled={loading || Boolean(loadError)}
        >
          <Icon name="plus" size={14} /> {t('settings.categories.create')}
        </button>
      </div>
      {(loading || loadError) && (
        <DataState
          loading={loading}
          error={loadError}
          onRetry={load}
          errorMessage={t('settings.error.loadCategories')}
        />
      )}

      {!loadError && (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: '12px',
            border: '1px solid var(--border)',
            boxShadow: 'none',
            overflow: 'hidden'
          }}
        >
          <Table className="data-table">
            <TableHead>
              <TableRow>
                <TableCell className="settings-table__th-index">#</TableCell>
                <TableCell>{t('settings.categories.name')}</TableCell>
                <TableCell>{t('settings.categories.guidance')}</TableCell>
                <TableCell className="settings-table__th-status">{t('common.status')}</TableCell>
                <TableCell className="settings-table__th-actions" align="center">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && items.length === 0 && <LoadingState />}
              {paginatedItems.map((item, idx) => (
                <TableRow key={item.id}>
                  <TableCell className="table-cell--index">
                    {page * rowsPerPage + idx + 1}
                  </TableCell>
                  <TableCell>
                    <div className="settings-category-cell">
                      <CategoryIcon icon={item.icon || 'tag'} color={item.color || '#6b7280'} size={22} />
                      <span className="settings-category-cell__name">{item.label}</span>
                    </div>
                  </TableCell>
                  <TableCell className="settings-category-cell__desc" title={item.description}>
                    {item.description || <em>{t('settings.categories.noDescription')}</em>}
                  </TableCell>
                  <TableCell><StatusBadge active={item.active !== false} /></TableCell>
                  <TableCell align="center">
                    <div className="settings-actions">
                      <button className="btn-ghost-sm" onClick={() => openEdit(item)} title={t('common.update')}><Icon name="edit" size={13} /></button>
                      <button className="btn-ghost-sm settings-actions__delete" onClick={() => setConfirm(item)} title={t('common.delete')}><Icon name="trash" size={13} /></button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && items.length === 0 && <EmptyState message={t('settings.categories.empty')} />}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            count={items.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: '1px solid var(--border)',
              '& .MuiTablePagination-toolbar': {
                minHeight: '52px'
              }
            }}
          />
        </TableContainer>
      )}

      {modal && (
        <Modal
          title={t(modal.mode === 'add' ? 'settings.categories.createTitle' : 'settings.categories.updateTitle')}
          subtitle={t(modal.mode === 'add' ? 'settings.categories.createSubtitle' : 'settings.categories.updateSubtitle')}
          icon={<Icon name={form.icon || 'tag'} size={18} style={{ color: form.color }} />}
          onClose={() => setModal(null)}
          width={720}
        >
          <ErrorBanner msg={formError} />
          <div className="flex-gap-16 flex-wrap">
            <div className="flex-1-min">
              <Field label={t('settings.categories.name')} required error={fieldErrors.category_name}>
                <IconField icon="tag">
                  <input
                    className="field-input field-input--icon"
                    value={form.category_name}
                    onChange={event => setCategoryFormField('category_name', event.target.value)}
                    placeholder={t('settings.categories.namePlaceholder')}
                    aria-invalid={Boolean(fieldErrors.category_name)}
                  />
                </IconField>
              </Field>
            </div>
            {modal.mode === 'edit' && (
              <div className="flex-1-min">
                <Field label={t('common.status')} error={fieldErrors.active}>
                  <Dropdown
                    value={(form.active ?? true) ? 'active' : 'inactive'}
                    onChange={value => setCategoryFormField('active', value === 'active')}
                    options={[
                      { value: 'active', label: 'Active', leading: <Icon name="check-circle" size={14} style={{ color: '#16a34a' }} /> },
                      { value: 'inactive', label: 'Inactive', leading: <Icon name="x-circle" size={14} /> },
                    ]}
                  />
                </Field>
              </div>
            )}
          </div>
          <div className="category-modal-grid">
            <div>
              <Field label={t('settings.categories.description')} error={fieldErrors.description}>
                <IconField icon="message" align="top">
                  <textarea
                    className="field-input field-input--textarea"
                    value={form.description}
                    onChange={event => setCategoryFormField('description', event.target.value)}
                    placeholder={t('settings.categories.descriptionPlaceholder')}
                    aria-invalid={Boolean(fieldErrors.description)}
                  />
                </IconField>
              </Field>
            </div>
            <div>
              <Field label={t('settings.categories.sample')} error={fieldErrors.sample}>
                <IconField icon="mail" align="top">
                  <textarea
                    className="field-input field-input--textarea"
                    value={form.sample}
                    onChange={event => setCategoryFormField('sample', event.target.value)}
                    placeholder={t('settings.categories.samplePlaceholder')}
                    aria-invalid={Boolean(fieldErrors.sample)}
                  />
                </IconField>
              </Field>
            </div>
          </div>
          <div className="category-modal-grid">
            <div>
              <Field label={t('settings.categories.icon')} error={fieldErrors.icon}>
                <IconPicker value={form.icon} onChange={icon => setCategoryFormField('icon', icon)} color={form.color} />
              </Field>
            </div>
            <div>
              <Field label={t('settings.categories.color')} error={fieldErrors.color}>
                <ColorPicker value={form.color} onChange={color => setCategoryFormField('color', color)} />
              </Field>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn-ghost" onClick={() => setModal(null)}>{t('common.cancel')}</button>
            <button className="btn-primary" onClick={save} disabled={saving}>
              {t(modal.mode === 'add'
                ? (saving ? 'common.creating' : 'common.create')
                : (saving ? 'common.updating' : 'common.update'))}
            </button>
          </div>
        </Modal>
      )}

      {confirm && <ConfirmDialog message={t('settings.categories.deleteConfirm', { name: confirm.label })} onConfirm={() => doDelete(confirm.id)} onCancel={() => setConfirm(null)} />}
      <AppToast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}

// â”€â”€ Main SettingsPage â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const SETTINGS_PANELS = {
  users: UsersTab,
  categories: CategoryTab,
};

export default function SettingsPage() {
  const { t } = useTranslation();
  const ActivePanel = SETTINGS_PANELS.users;

  return (
    <Box component="section" aria-labelledby="settings-title" className="settings-page page-stack">
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
          <ManageAccountsOutlinedIcon color="primary" sx={{ mt: 0.25 }} />
          <Box>
            <Typography id="settings-title" component="h1" variant="h6">
              {t('settings.title')}
            </Typography>
          </Box>
        </Stack>
      </Paper>
      <ActivePanel />
    </Box>
  );
}
